import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import type { Invoice } from './useBilling.js';

// ── Cobro por pasarela a los clientes de la cuenta ───────────────────────────
// Fase 4 (2026-08-31) — docs/PLAN_cobro_a_clientes.md.
//
// Un `RecurringCharge` es el ACUERDO (una suscripción de Stripe); las facturas
// son los documentos que ese acuerdo emite, uno por período. Cambiarle el monto
// o cancelarlo afecta lo que se emita de ahora en adelante, nunca una factura
// ya emitida.

export interface RecurringCharge {
  id: number;
  contactId: number;
  /** Nombre del cliente, resuelto por el servidor en la misma consulta que el
   *  documento. null = el contacto no existe o no tiene nombre usable. */
  contactName: string | null;
  projectId: number | null;
  sourceInvoiceId: number | null;
  description: string;
  amountCents: number;
  currency: string;
  interval: string;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  stripePriceId: string | null;
  /** 'pending_card' = creado, esperando que el cliente cargue la tarjeta. */
  status: 'pending_card' | 'active' | 'past_due' | 'canceled';
  currentPeriodEnd: number | null;
  cardBrand: string | null;
  cardLast4: string | null;
  cardStatus: 'ok' | 'failing' | 'none';
  canceledAt: number | null;
  createdAt: number;
  updatedAt: number;
}

export interface InvoicePayment {
  id: number;
  invoiceId: number;
  recurringChargeId: number | null;
  stripeObjectId: string;
  kind: 'checkout' | 'cycle';
  status: 'succeeded' | 'failed';
  amountCents: number;
  currency: string;
  occurredAt: number;
  failureCode: string | null;
  failureMessage: string | null;
  receiptUrl: string | null;
  createdAt: number;
}

/** ¿Esta cuenta cobra por pasarela, o solo factura y marca a mano? Sin esto la
 *  UI mostraría opciones de cobro que el servidor va a rechazar. */
export interface PaymentCapability {
  canCharge: boolean;
  /** 'test' = Stripe no está moviendo dinero real. Lo consume el aviso
   *  permanente de Facturación — ver StripeTestModeBanner. */
  stripeMode: 'test' | 'live';
}

export function usePaymentCapability() {
  return useQuery<PaymentCapability>({
    queryKey: ['billing-payment-capability'],
    queryFn: () => api.get<PaymentCapability>('/api/billing/payment-capability'),
    staleTime: 5 * 60 * 1000,
  });
}

export function useRecurringCharges() {
  return useQuery<{ charges: RecurringCharge[] }>({
    queryKey: ['billing-recurring-charges'],
    queryFn: () => api.get<{ charges: RecurringCharge[] }>('/api/billing/recurring-charges'),
  });
}

export function useInvoicePayments(invoiceId: number | undefined) {
  return useQuery<{ payments: InvoicePayment[] }>({
    queryKey: ['billing-invoice-payments', invoiceId],
    queryFn: () => api.get<{ payments: InvoicePayment[] }>(`/api/billing/invoices/${invoiceId}/payments`),
    enabled: invoiceId !== undefined,
  });
}

export function useSetInvoicePaymentMode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, mode, interval }: { id: number; mode: Invoice['paymentMode']; interval?: 'month' | 'year' }) =>
      api.patch<Invoice>(`/api/billing/invoices/${id}/payment-mode`, { mode, ...(interval ? { interval } : {}) }),
    onSuccess: invoice => {
      void qc.invalidateQueries({ queryKey: ['billing-invoice', invoice.id] });
      void qc.invalidateQueries({ queryKey: ['billing-invoices'] });
      void qc.invalidateQueries({ queryKey: ['billing-recurring-charges'] });
    },
  });
}

/** El monto nuevo vale desde el PRÓXIMO período — Stripe no prorratea el tramo
 *  ya transcurrido. Y no le avisa nada al cliente: ese aviso es un borrador
 *  aparte que se revisa antes de mandar. */
export function useChangeChargeAmount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, amountCents }: { id: number; amountCents: number }) =>
      api.patch<RecurringCharge>(`/api/billing/recurring-charges/${id}/amount`, { amountCents }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['billing-recurring-charges'] });
    },
  });
}

/**
 * Prepara el aviso de cambio de monto — BORRADOR, no lo envía.
 *
 * Cambiar el precio y avisarle al cliente son dos acciones separadas a
 * propósito: el aviso lleva contexto (por qué sube, desde cuándo) que el
 * sistema no tiene. Claude lo redacta con los números correctos y la persona
 * lo aprueba, igual que el resto del outreach.
 *
 * El borrador queda colgado de la factura de origen del acuerdo, que es donde
 * ya se revisan los demás correos de esa factura.
 */
export function useComposeAmountChangeNotice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason?: string }) =>
      api.post<{ draftId: string; subject: string; invoiceId: number }>(
        `/api/billing/recurring-charges/${id}/outreach/compose-amount-change`,
        { ...(reason ? { reason } : {}) },
      ),
    onSuccess: result => {
      void qc.invalidateQueries({ queryKey: ['invoice-outreach-drafts', result.invoiceId] });
    },
  });
}

/** Corta el futuro. Las facturas ya emitidas quedan intactas: las pagadas
 *  siguen pagadas y las pendientes siguen cobrables con su propio link. */
export function useCancelRecurringCharge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, immediately }: { id: number; immediately?: boolean }) =>
      api.post<RecurringCharge>(`/api/billing/recurring-charges/${id}/cancel`, { immediately: immediately ?? false }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['billing-recurring-charges'] });
      void qc.invalidateQueries({ queryKey: ['billing-invoices'] });
    },
  });
}
